/**
 * Analyzer (Phase 11) — linear 4-step flow matching the user's hand-drawn diagram.
 *
 *   1. DATOS            → project metadata
 *   2. REQUERIMIENTO    → what to analyze (checkboxes)
 *   3. ACTIVIDADES      → sub-activities + subsystems (accordion)
 *   4. REPORTE          → what to export (PDF + optional Excel, BOM, Estimado)
 *
 * The app already has the CRUD plumbing (project/survey/annotator/etc). This
 * page is a top-level orchestrator: a progressive-disclosure checklist that
 * drives the user from "create project" to "deliver report" without tabs.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Check, ChevronDown, ChevronRight, Loader2,
  FileText, ClipboardList, Layers, FileOutput,
  UploadCloud, Ruler, Hash, PencilLine, Wand2, Sparkles,
  Download,
} from "lucide-react";
import api from "@/lib/api";

// --- Static option trees ---------------------------------------------------
const REQUIREMENTS = [
  { key: "site_survey", icon: ClipboardList, es: "Site Survey", en: "Site Survey" },
  { key: "planos",      icon: FileText,      es: "Planos",      en: "Blueprints" },
  { key: "imagenes",    icon: UploadCloud,   es: "Imágenes",    en: "Images" },
  { key: "sow",         icon: FileText,      es: "SOW",         en: "SOW" },
  { key: "videos",      icon: UploadCloud,   es: "Videos",      en: "Videos" },
];

const TAREAS = [
  { key: "mediciones", es: "Mediciones", en: "Measurements", icon: Ruler },
  { key: "cantidades", es: "Cantidades", en: "Counts", icon: Hash },
  { key: "mod_planos", es: "Modificación Planos", en: "Plan Modifications", icon: PencilLine,
    sub: [
      { key: "dibujar_trazos", es: "Dibujar Trazos", en: "Draw Routes" },
      { key: "insertar_iconos", es: "Insertar Iconos", en: "Insert Icons" },
      { key: "insertar_marquillas", es: "Insertar Marquillas", en: "Insert Labels" },
      { key: "ia_trazos", es: "IA Trazos (propuesta IA)", en: "AI Routes (AI proposal)" },
    ]},
  { key: "ia_diseno", es: "IA Diseño", en: "AI Design", icon: Wand2 },
];

const SUBSYSTEMS_LV = [
  { key: "audio",          es: "Audio", en: "Audio", color: "#F59E0B" },
  { key: "video",          es: "Video", en: "Video", color: "#EC4899" },
  { key: "cctv",           es: "CCTV",  en: "CCTV",  color: "#DC2626" },
  { key: "wap",            es: "WAP's / WiFi", en: "WAPs / WiFi", color: "#8B5CF6" },
  { key: "data",           es: "Datos", en: "Data", color: "#2563EB" },
  { key: "fiber",          es: "Fibra Óptica", en: "Fiber Optic", color: "#06B6D4" },
  { key: "access",         es: "Control de Acceso", en: "Access Control", color: "#0891B2" },
  { key: "fire_alarm",     es: "Fire Alarm", en: "Fire Alarm", color: "#EF4444" },
  { key: "mdf",            es: "MDF (Rack · Escalerilla)", en: "MDF (Rack · Ladder)", color: "#003366" },
  { key: "idf",            es: "IDF (Rack · Escalerilla)", en: "IDF (Rack · Ladder)", color: "#1E40AF" },
];

const SUBSYSTEMS_EL = [
  { key: "power",      es: "Distribución Eléctrica", en: "Power Distribution", color: "#EA580C" },
  { key: "lighting",   es: "Iluminación",            en: "Lighting",           color: "#EAB308" },
  { key: "devices",    es: "Tomas / Interruptores",  en: "Outlets / Switches", color: "#7C3AED" },
  { key: "grounding",  es: "Puesta a Tierra",        en: "Grounding",          color: "#78716C" },
  { key: "generator",  es: "Generador / Respaldo",   en: "Backup / Generator", color: "#16A34A" },
  { key: "conduit",    es: "Conduit / Cableado",     en: "Conduit / Wiring",   color: "#0EA5E9" },
];

const REPORT_OPTIONS = [
  { key: "planos_record", es: "Planos Record (anotados)", en: "Record Plans (annotated)", always_pdf: true },
  { key: "bom",           es: "BOM · Lista de materiales (sin precios)", en: "BOM · Material list (no prices)", always_pdf: true },
  { key: "cronograma",    es: "Cronograma",                en: "Schedule",            always_pdf: true },
  { key: "estimado",      es: "Estimado (con precios $)",  en: "Estimate (with prices $)", always_pdf: true },
  { key: "excel",         es: "Excel adjunto", en: "Excel attachment", always_pdf: false },
];

// --- Main component --------------------------------------------------------
export default function Analyzer() {
  const { projectId } = useParams();
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openStep, setOpenStep] = useState(1); // 0=Datos, 1=Requerimiento, 2=Actividades, 3=Reporte

  // Step data
  const [requirements, setRequirements] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [subtareas, setSubtareas] = useState([]);
  const [subsystems, setSubsystems] = useState([]);
  const [reports, setReports] = useState(["planos_record", "bom"]);

  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [xlsxing, setXlsxing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p] = await Promise.all([
          api.get(`/projects/${projectId}`).then(r => r.data),
        ]);
        setProject(p);
        // Pre-load any persisted analyzer state
        const st = p?.analyzer_state || {};
        if (st.requirements) setRequirements(st.requirements);
        if (st.tareas)       setTareas(st.tareas);
        if (st.subtareas)    setSubtareas(st.subtareas);
        if (st.subsystems)   setSubsystems(st.subsystems);
        if (st.reports)      setReports(st.reports);
      } finally { setLoading(false); }
    })();
  }, [projectId]);

  const module = project?.module || "low_voltage";
  const subsysList = module === "electrical" ? SUBSYSTEMS_EL : SUBSYSTEMS_LV;
  const moduleLabel = module === "electrical"
    ? (es ? "Eléctrico" : "Electrical")
    : (es ? "Bajo Voltaje" : "Low Voltage");
  const moduleColor = module === "electrical" ? "#EAB308" : "#003366";

  // Auto-save state as user changes selections
  useEffect(() => {
    if (loading) return;
    const st = { requirements, tareas, subtareas, subsystems, reports };
    const handle = setTimeout(() => {
      api.patch(`/projects/${projectId}/analyzer-state`, st).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
  }, [requirements, tareas, subtareas, subsystems, reports, loading, projectId]);

  const toggle = (list, setter, key) =>
    setter(list.includes(key) ? list.filter(k => k !== key) : [...list, key]);

  const canProceed = (step) => {
    if (step === 0) return true;
    if (step === 1) return requirements.length > 0;
    if (step === 2) return tareas.length > 0 || subsystems.length > 0;
    if (step === 3) return reports.length > 0;
    return true;
  };

  const generateReport = async () => {
    setExporting(true);
    setSummary(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/analyzer/report`, {
        requirements, tareas, subtareas, subsystems, reports,
        language: es ? "es" : "en",
      }, { responseType: "blob" });
      // Try to download as PDF
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analyzer-${project?.name || projectId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setSummary({ ok: true, ts: new Date() });
    } catch (e) {
      setSummary({ error: e?.response?.data?.detail || "Error" });
    } finally { setExporting(false); }
  };

  const emailReport = async () => {
    const toEmail = project?.contact_email
      || prompt(es ? "Correo del cliente:" : "Client email:", "");
    if (!toEmail) return;
    const msg = prompt(
      es ? "Mensaje al cliente (opcional):" : "Message to client (optional):",
      es ? "Hola, adjunto el análisis del proyecto." : "Hi, attached is the project analysis."
    );
    setEmailing(true);
    setSummary(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/analyzer/email-report`, {
        requirements, tareas, subtareas, subsystems, reports,
        language: es ? "es" : "en",
        to_email: toEmail,
        message: msg,
      });
      setSummary({ emailed: true, to: data.to, ts: new Date(), share_url: data.share_url });
      if (data.share_url) setShareLink({ url: data.share_url, expires_at: data.share_expires_at });
    } catch (e) {
      setSummary({ error: e?.response?.data?.detail || "Email failed" });
    } finally { setEmailing(false); }
  };

  const generateShareLink = async () => {
    setSharing(true);
    setSummary(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/analyzer/share-link`, {
        requirements, tareas, subtareas, subsystems, reports,
        language: es ? "es" : "en",
      });
      setShareLink({ url: data.url, expires_at: data.expires_at, days: data.days });
      setSummary({ linked: true, url: data.url, ts: new Date() });
    } catch (e) {
      setSummary({ error: e?.response?.data?.detail || "Share-link failed" });
    } finally { setSharing(false); }
  };

  const copyShareLink = async () => {
    if (!shareLink?.url) return;
    try {
      await navigator.clipboard.writeText(shareLink.url);
      setSummary({ copied: true, ts: new Date() });
    } catch {
      window.prompt(es ? "Copia el link:" : "Copy the link:", shareLink.url);
    }
  };

  const downloadXlsx = async () => {
    setXlsxing(true);
    setSummary(null);
    try {
      const params = new URLSearchParams({
        lang: es ? "es" : "en",
        prices: reports.includes("estimado") ? "1" : "0",
      });
      if (subsystems.length) params.set("subsystems", subsystems.join(","));
      const { data } = await api.get(
        `/projects/${projectId}/analyzer/bom.xlsx?${params.toString()}`,
        { responseType: "blob" }
      );
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bom-${project?.name || projectId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setSummary({ xlsx: true, ts: new Date() });
    } catch (e) {
      setSummary({ error: e?.response?.data?.detail || "Excel export failed" });
    } finally { setXlsxing(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#003366]" /></div>;
  }
  if (!project) {
    return <div className="p-6 text-sm text-slate-500">Project not found</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="analyzer-page">
      {/* Header */}
      <Link to={`/projects/${projectId}`}
            className="text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-[#003366]">
        ← {es ? "Volver al proyecto" : "Back to project"}
      </Link>
      <div className="flex items-start justify-between mt-2 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#003366] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: moduleColor }} />
            {moduleLabel} · ANALYZER
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">{project.name}</h1>
          <p className="text-sm text-slate-600">
            {es ? "Configura qué analizar, qué tareas ejecutar y qué reporte entregar."
                : "Configure what to analyze, which tasks to run, and what report to deliver."}
          </p>
        </div>
      </div>

      {/* Step 1 — Datos */}
      <Step number={0} open={openStep === 0} onOpen={() => setOpenStep(0)} title={es ? "Datos" : "Data"}
            summary={`${project.name || "—"} · ${project.company || project.client || "—"}`}
            completed testid="step-datos">
        <dl className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Row lbl={es ? "Compañía" : "Company"} val={project.company || project.client} />
          <Row lbl="E-mail" val={project.contact_email} />
          <Row lbl={es ? "Nombre" : "Name"} val={project.name} highlight />
          <Row lbl={es ? "Inicio" : "Start"} val={project.start_date} />
          <Row lbl={es ? "Fin" : "End"} val={project.end_date} />
          <Row lbl={es ? "Ubicación" : "Location"} val={project.location} />
        </dl>
      </Step>

      {/* Step 2 — Requerimiento */}
      <Step number={1} open={openStep === 1} onOpen={() => setOpenStep(1)}
            title={es ? "Requerimiento — ¿Qué analizar?" : "Requirement — What to analyze?"}
            summary={`${requirements.length} ${es ? "tipos" : "types"}`}
            completed={requirements.length > 0} testid="step-requerimiento">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="requirement-grid">
          {REQUIREMENTS.map(r => {
            const active = requirements.includes(r.key);
            return (
              <button key={r.key} onClick={() => toggle(requirements, setRequirements, r.key)}
                data-testid={`req-${r.key}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-colors ${
                  active ? "border-[#003366] bg-[#003366]/5" : "border-slate-200 hover:bg-slate-50"
                }`}>
                <r.icon size={14} className={active ? "text-[#003366]" : "text-slate-400"} />
                <span className="flex-1 text-left">{es ? r.es : r.en}</span>
                {active && <Check size={12} className="text-emerald-600" />}
              </button>
            );
          })}
        </div>
      </Step>

      {/* Step 3 — Actividades */}
      <Step number={2} open={openStep === 2} onOpen={() => setOpenStep(2)}
            title={es ? "Actividades" : "Activities"}
            summary={`${tareas.length} ${es ? "tareas" : "tasks"} · ${subsystems.length} ${es ? "subsistemas" : "subsystems"}`}
            completed={tareas.length > 0 || subsystems.length > 0} testid="step-actividades">
        <div className="mb-5">
          <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
            {es ? "Tareas" : "Tasks"}
          </h4>
          <div className="space-y-1.5" data-testid="tareas-list">
            {TAREAS.map(t => {
              const active = tareas.includes(t.key);
              return (
                <div key={t.key} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button onClick={() => toggle(tareas, setTareas, t.key)}
                    data-testid={`tarea-${t.key}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                      active ? "bg-[#003366]/5" : "hover:bg-slate-50"
                    }`}>
                    <t.icon size={14} className={active ? "text-[#003366]" : "text-slate-400"} />
                    <span className="flex-1 text-left">{es ? t.es : t.en}</span>
                    {active && <Check size={12} className="text-emerald-600" />}
                  </button>
                  {active && t.sub && (
                    <div className="border-t border-slate-100 bg-slate-50/50 p-2" data-testid={`subtareas-${t.key}`}>
                      <div className="grid grid-cols-2 gap-1">
                        {t.sub.map(s => {
                          const on = subtareas.includes(s.key);
                          return (
                            <button key={s.key} onClick={() => toggle(subtareas, setSubtareas, s.key)}
                              data-testid={`subtarea-${s.key}`}
                              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                                on ? "bg-[#EA580C]/10 text-[#EA580C]" : "text-slate-600 hover:bg-white"
                              }`}>
                              {on ? <Check size={10} /> : <span className="w-2.5 h-2.5 border border-slate-300 rounded-sm" />}
                              <span className="flex-1 text-left">{es ? s.es : s.en}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
            {es ? `Subsistemas (${moduleLabel})` : `Subsystems (${moduleLabel})`}
          </h4>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="subsystems-grid">
            {subsysList.map(s => {
              const active = subsystems.includes(s.key);
              return (
                <button key={s.key} onClick={() => toggle(subsystems, setSubsystems, s.key)}
                  data-testid={`sub-${s.key}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${
                    active ? "border-slate-400 bg-slate-50" : "border-slate-200 hover:bg-slate-50"
                  }`}>
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="flex-1 text-left">{es ? s.es : s.en}</span>
                  {active && <Check size={12} className="text-emerald-600" />}
                </button>
              );
            })}
          </div>
        </div>
      </Step>

      {/* Step 4 — Reporte */}
      <Step number={3} open={openStep === 3} onOpen={() => setOpenStep(3)}
            title={es ? "Reporte — ¿Qué entregar?" : "Report — What to deliver?"}
            summary={`${reports.length} ${es ? "componentes" : "components"}`}
            completed={reports.length > 0} testid="step-reporte">
        <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
          {es
            ? "El reporte final sale en PDF. El BOM y el Cronograma salen del análisis automático según los subsistemas que selecciones. El Estimado (con precios $) es opcional."
            : "Final report exports as PDF. The BOM and Schedule come from the automatic analysis based on the subsystems you selected. Estimate (with $) is optional."}
        </div>
        <div className="space-y-1.5" data-testid="report-options">
          {REPORT_OPTIONS.map(r => {
            const active = reports.includes(r.key);
            return (
              <button key={r.key} onClick={() => toggle(reports, setReports, r.key)}
                data-testid={`report-${r.key}`}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-colors ${
                  active ? "border-[#003366] bg-[#003366]/5" : "border-slate-200 hover:bg-slate-50"
                }`}>
                {active
                  ? <Check size={13} className="text-emerald-600" />
                  : <span className="w-3 h-3 border border-slate-300 rounded-sm" />}
                <span className="flex-1 text-left">{es ? r.es : r.en}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                  {r.always_pdf ? "PDF" : "attach"}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-slate-500">
            {es ? "Genera el PDF, compártelo con un link firmado o envíalo por email."
                : "Generate the PDF, share with a signed link, or send by email."}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={generateShareLink} disabled={sharing || !reports.length}
              data-testid="share-link-btn"
              className="px-4 py-2.5 bg-white border border-emerald-600 text-emerald-700 rounded-xl text-sm font-medium hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-2">
              {sharing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {es ? "Link al cliente" : "Client link"}
            </button>
            <button onClick={downloadXlsx} disabled={xlsxing}
              data-testid="download-xlsx-btn"
              className="px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-2">
              {xlsxing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Excel
            </button>
            <button onClick={emailReport} disabled={emailing || !reports.length}
              data-testid="email-report-btn"
              className="px-4 py-2.5 bg-white border border-[#003366] text-[#003366] rounded-xl text-sm font-medium hover:bg-[#003366]/5 disabled:opacity-50 inline-flex items-center gap-2">
              {emailing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {es ? "Enviar email" : "Send email"}
            </button>
            <button onClick={generateReport} disabled={exporting || !reports.length}
              data-testid="generate-report-btn"
              className="px-5 py-2.5 bg-[#EA580C] text-white rounded-xl text-sm font-medium hover:bg-[#C2410C] disabled:opacity-50 inline-flex items-center gap-2 shadow-sm">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {es ? "Descargar PDF" : "Download PDF"}
            </button>
          </div>
        </div>

        {shareLink && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="share-link-box">
            <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 mb-1">
              {es ? "Link firmado · expira en" : "Signed link · expires in"} {shareLink.days || 7} {es ? "días" : "days"}
            </div>
            <div className="flex items-center gap-2">
              <input readOnly value={shareLink.url} data-testid="share-link-input"
                className="flex-1 text-xs font-mono bg-white border border-emerald-200 rounded-lg px-2 py-1.5 text-slate-700" />
              <button onClick={copyShareLink} data-testid="share-link-copy"
                className="text-xs font-mono uppercase tracking-wider bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700">
                {es ? "Copiar" : "Copy"}
              </button>
              <a href={shareLink.url} target="_blank" rel="noreferrer"
                 data-testid="share-link-open"
                 className="text-xs font-mono uppercase tracking-wider border border-emerald-300 text-emerald-700 px-3 py-1.5 rounded-lg hover:bg-white">
                {es ? "Abrir" : "Open"}
              </a>
            </div>
          </div>
        )}
        {summary?.ok && (
          <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl p-3" data-testid="report-ok">
            ✓ {es ? "Reporte descargado" : "Report downloaded"} — {summary.ts.toLocaleTimeString()}
          </div>
        )}
        {summary?.emailed && (
          <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl p-3" data-testid="report-emailed">
            ✓ {es ? "Enviado a" : "Sent to"} <b>{summary.to}</b> — {summary.ts.toLocaleTimeString()}
          </div>
        )}
        {summary?.error && (
          <div className="mt-3 text-xs bg-red-50 border border-red-200 text-red-900 rounded-xl p-3" data-testid="report-error">
            {summary.error}
          </div>
        )}
      </Step>
    </div>
  );
}

// --- Presentational bits ---------------------------------------------------
function Step({ number, title, summary, open, onOpen, completed, children, testid }) {
  return (
    <div className="iqes-card mb-3 overflow-hidden" data-testid={testid}>
      <button onClick={onOpen}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono font-semibold flex-shrink-0 ${
          completed ? "bg-emerald-500 text-white" :
          open      ? "bg-[#003366] text-white" :
          "bg-slate-200 text-slate-500"
        }`}>
          {completed && !open ? <Check size={14} /> : number + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900">{title}</div>
          <div className="text-xs font-mono text-slate-500 truncate">{summary}</div>
        </div>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && <div className="p-4 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
}

function Row({ lbl, val, highlight }) {
  return (
    <>
      <dt className="text-xs font-mono uppercase tracking-wider text-slate-500 self-center">{lbl}</dt>
      <dd className={`text-sm ${highlight ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {val || <span className="italic text-slate-400">—</span>}
      </dd>
    </>
  );
}
